import {
  AcUnit as AcUnitIcon,
  ArrowBack as ArrowBackIcon,
  Block as BlockIcon,
  Campaign as CampaignIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Forward as ForwardIcon,
  LocalFireDepartment as LocalFireDepartmentIcon,
  Person as PersonIcon,
  ReportProblem as ReportProblemIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../store";
import { selectUserNo } from "../../store/slices/authSlice";
import {
  BrazeCurrentEventType,
  BrazeCurrentEventTypeLabels,
  CurrentQueueDetailItem,
  CurrentQueueDetailSearchRequest,
  SalesSummaryByCampaign,
  SalesSummaryCustomer,
  useGetSalesSummaryQuery,
  useSearchCurrentQueueDetailsQuery,
} from "../../store/services/currentQueueDetailService";

type ViewMode = "summary" | "details";
type SortField = CurrentQueueDetailSearchRequest["sortBy"];
type EngagementStatus =
  | "all"
  | "never_opened"
  | "opened_not_clicked"
  | "clicked"
  | "negative";

const ENGAGEMENT_OPTIONS: { value: EngagementStatus; label: string }[] = [
  { value: "all", label: "Alla" },
  { value: "never_opened", label: "Ej öppnat" },
  { value: "opened_not_clicked", label: "Öppnat" },
  { value: "clicked", label: "Klickat" },
  { value: "negative", label: "Negativt" },
];

const getDefaultDates = () => {
  const now = new Date();
  const from = new Date(now);
  from.setMonth(from.getMonth() - 3);
  return {
    fromDate: from.toISOString().slice(0, 10),
    toDate: now.toISOString().slice(0, 10),
  };
};

const MyContactsView: React.FC = () => {
  const userNo = useSelector(selectUserNo);
  const selectedUserId = useSelector(
    (state: RootState) => state.user.selectedUserId
  );
  const activePerson = selectedUserId ?? (userNo ? Number(userNo) : null);

  const [dates, setDates] = useState(getDefaultDates);
  const [hideEmpty, setHideEmpty] = useState(true);
  const [engagement, setEngagement] = useState<EngagementStatus>("all");
  const [hotOnly, setHotOnly] = useState(false);
  const [withContactOnly, setWithContactOnly] = useState(false);
  const [minSends, setMinSends] = useState<number>(0);
  const [viewMode, setViewMode] = useState<ViewMode>("summary");
  const [drillDownCustomerNo, setDrillDownCustomerNo] = useState<
    number | null
  >(null);

  const handleDrillDown = (customerNo: number) => {
    setDrillDownCustomerNo(customerNo);
    setViewMode("details");
  };

  const handleBackToSummary = () => {
    setDrillDownCustomerNo(null);
    setViewMode("summary");
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto" }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <TextField
              label="Från"
              type="date"
              size="small"
              value={dates.fromDate}
              onChange={(e) =>
                setDates((prev) => ({ ...prev, fromDate: e.target.value }))
              }
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Till"
              type="date"
              size="small"
              value={dates.toDate}
              onChange={(e) =>
                setDates((prev) => ({ ...prev, toDate: e.target.value }))
              }
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <Chip
              icon={<FilterListIcon />}
              label="Dölj utan event"
              variant={hideEmpty ? "filled" : "outlined"}
              color={hideEmpty ? "primary" : "default"}
              onClick={() => setHideEmpty((prev) => !prev)}
            />
          </Stack>

          <Divider />

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <ToggleButtonGroup
              size="small"
              value={engagement}
              exclusive
              onChange={(_, v: EngagementStatus | null) =>
                v && setEngagement(v)
              }
            >
              {ENGAGEMENT_OPTIONS.map((o) => (
                <ToggleButton key={o.value} value={o.value} sx={{ px: 1.5 }}>
                  {o.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Chip
              icon={<LocalFireDepartmentIcon />}
              label="Bara heta"
              variant={hotOnly ? "filled" : "outlined"}
              color={hotOnly ? "warning" : "default"}
              onClick={() => setHotOnly((prev) => !prev)}
            />
            <Chip
              icon={<PersonIcon />}
              label="Med kontaktperson"
              variant={withContactOnly ? "filled" : "outlined"}
              color={withContactOnly ? "primary" : "default"}
              onClick={() => setWithContactOnly((prev) => !prev)}
            />
            <TextField
              label="Min sends"
              type="number"
              size="small"
              value={minSends || ""}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                setMinSends(Number.isFinite(v) && v > 0 ? v : 0);
              }}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { min: 0, max: 999, step: 1 },
              }}
              sx={{ width: 100 }}
              placeholder="0"
            />
          </Stack>
        </Stack>
      </Paper>

      {!activePerson ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            Välj en användare via knappen nere till höger
          </Typography>
        </Paper>
      ) : viewMode === "summary" ? (
        <SummaryView
          salesResponsibleNo={activePerson}
          fromDate={`${dates.fromDate}T00:00:00`}
          toDate={`${dates.toDate}T23:59:59`}
          hideEmpty={hideEmpty}
          engagement={engagement}
          hotOnly={hotOnly}
          withContactOnly={withContactOnly}
          minSends={minSends}
          onDrillDown={handleDrillDown}
        />
      ) : (
        <DetailsView
          customerNo={drillDownCustomerNo}
          fromDate={`${dates.fromDate}T00:00:00`}
          toDate={`${dates.toDate}T23:59:59`}
          onBack={handleBackToSummary}
        />
      )}
    </Box>
  );
};

// =============================================================================
// Summary view
// =============================================================================

interface SummaryViewProps {
  salesResponsibleNo: number;
  fromDate: string;
  toDate: string;
  hideEmpty: boolean;
  engagement: EngagementStatus;
  hotOnly: boolean;
  withContactOnly: boolean;
  minSends: number;
  onDrillDown: (customerNo: number) => void;
}

const SummaryView: React.FC<SummaryViewProps> = ({
  salesResponsibleNo,
  fromDate,
  toDate,
  hideEmpty,
  engagement,
  hotOnly,
  withContactOnly,
  minSends,
  onDrillDown,
}) => {
  const [expandedCustomer, setExpandedCustomer] = useState<number | null>(null);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);

  const {
    data: summary,
    isLoading,
    isFetching,
  } = useGetSalesSummaryQuery({ salesResponsibleNo, fromDate, toDate });

  const campaignRollup = useMemo(
    () => (summary ? aggregateCampaigns(summary.Customers) : []),
    [summary]
  );

  const analysis = useMemo(
    () => (summary ? analyzeCustomers(summary.Customers) : new Map()),
    [summary]
  );

  const allCampaignOptions = useMemo(
    () =>
      campaignRollup.map((c) => ({
        id: c.CampaignId,
        name: c.CampaignName,
        count: c.Count,
      })),
    [campaignRollup]
  );

  const selectedOptions = useMemo(
    () =>
      allCampaignOptions.filter((o) => selectedCampaignIds.includes(o.id)),
    [allCampaignOptions, selectedCampaignIds]
  );

  if (isLoading) {
    return (
      <Stack spacing={1}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={56} />
        ))}
      </Stack>
    );
  }

  if (!summary) return null;

  let customers = summary.Customers;
  if (hideEmpty) customers = customers.filter((c) => c.TotalQueueItems > 0);
  if (withContactOnly)
    customers = customers.filter((c) => c.ContactPersons.length > 0);
  if (minSends > 0) {
    customers = customers.filter(
      (c) => (analysis.get(c.CustomerNo)?.stats.sent ?? 0) >= minSends
    );
  }
  if (engagement !== "all") {
    customers = customers.filter((c) =>
      matchesEngagement(engagement, analysis.get(c.CustomerNo)?.stats)
    );
  }
  if (hotOnly) {
    customers = customers.filter(
      (c) => analysis.get(c.CustomerNo)?.flags.hot
    );
  }
  if (selectedCampaignIds.length > 0) {
    customers = customers.filter((c) =>
      c.ByCampaign?.some((b) => selectedCampaignIds.includes(b.CampaignId))
    );
  }

  return (
    <>
      {isFetching && <LinearProgress sx={{ mb: 1 }} />}

      {campaignRollup.length > 0 && (
        <CampaignRollupCard
          rollup={campaignRollup}
          selectedIds={selectedCampaignIds}
          onToggle={(id) =>
            setSelectedCampaignIds((prev) =>
              prev.includes(id)
                ? prev.filter((x) => x !== id)
                : [...prev, id]
            )
          }
        />
      )}

      {allCampaignOptions.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Autocomplete
            multiple
            size="small"
            options={allCampaignOptions}
            value={selectedOptions}
            onChange={(_, values) =>
              setSelectedCampaignIds(values.map((v) => v.id))
            }
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ width: "100%" }}
                >
                  <Typography variant="body2">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.count}
                  </Typography>
                </Stack>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filtrera på kampanj"
                placeholder={
                  selectedOptions.length === 0 ? "Välj en eller flera" : ""
                }
              />
            )}
          />
        </Paper>
      )}

      {customers.map((customer) => (
        <CustomerAccordion
          key={customer.CustomerNo}
          customer={customer}
          expanded={expandedCustomer === customer.CustomerNo}
          fromDate={fromDate}
          toDate={toDate}
          highlightedCampaignIds={selectedCampaignIds}
          analysis={analysis.get(customer.CustomerNo)}
          onToggle={() =>
            setExpandedCustomer((prev) =>
              prev === customer.CustomerNo ? null : customer.CustomerNo
            )
          }
          onDrillDown={() => onDrillDown(customer.CustomerNo)}
        />
      ))}

      {customers.length === 0 && (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            Inga kunder hittades för vald period
          </Typography>
        </Paper>
      )}
    </>
  );
};

// =============================================================================
// Campaign rollup card
// =============================================================================

interface CampaignRollupEntry {
  CampaignId: string;
  CampaignName: string;
  Count: number;
  CustomerCount: number;
  Sent: number;
  Open: number;
  Click: number;
}

interface CampaignRollupCardProps {
  rollup: CampaignRollupEntry[];
  selectedIds: string[];
  onToggle: (id: string) => void;
}

const CampaignRollupCard: React.FC<CampaignRollupCardProps> = ({
  rollup,
  selectedIds,
  onToggle,
}) => {
  const theme = useTheme();
  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <CampaignIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" fontWeight={600}>
          Kampanjer ({rollup.length})
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Klicka för att filtrera kundlistan
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {rollup.map((c) => {
          const active = selectedIds.includes(c.CampaignId);
          const openRate = c.Sent ? Math.round((c.Open / c.Sent) * 100) : 0;
          const clickRate = c.Sent ? Math.round((c.Click / c.Sent) * 100) : 0;
          return (
            <Tooltip
              key={c.CampaignId}
              title={
                <Stack spacing={0.25}>
                  <Typography variant="caption">
                    Skickat: {c.Sent}
                  </Typography>
                  <Typography variant="caption">
                    Öppnade: {c.Open} ({openRate}%)
                  </Typography>
                  <Typography variant="caption">
                    Klick: {c.Click} ({clickRate}%)
                  </Typography>
                </Stack>
              }
            >
              <Chip
                label={`${c.CampaignName} — ${c.CustomerCount} kund${
                  c.CustomerCount === 1 ? "" : "er"
                }, ${c.Count} event`}
                size="small"
                onClick={() => onToggle(c.CampaignId)}
                variant={active ? "filled" : "outlined"}
                color={active ? "primary" : "default"}
                sx={{
                  bgcolor: active
                    ? undefined
                    : alpha(theme.palette.primary.main, 0.04),
                }}
              />
            </Tooltip>
          );
        })}
      </Stack>
    </Paper>
  );
};

// =============================================================================
// Customer accordion — types shown inline, details fetched on expand
// =============================================================================

interface CustomerAccordionProps {
  customer: SalesSummaryCustomer;
  expanded: boolean;
  fromDate: string;
  toDate: string;
  highlightedCampaignIds: string[];
  analysis?: CustomerAnalysis;
  onToggle: () => void;
  onDrillDown: () => void;
}

const CustomerAccordion: React.FC<CustomerAccordionProps> = ({
  customer,
  expanded,
  fromDate,
  toDate,
  highlightedCampaignIds,
  analysis,
  onToggle,
  onDrillDown,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const contactPersonNos = useMemo(
    () => customer.ContactPersons.map((cp) => cp.ContactPersonNo),
    [customer.ContactPersons]
  );

  const { data: details, isFetching: detailsFetching } =
    useSearchCurrentQueueDetailsQuery(
      {
        contactPersonNos,
        fromDate,
        toDate,
        page: 1,
        pageSize: 50,
        sortBy: "created",
        sortDir: "desc",
      },
      { skip: !expanded || contactPersonNos.length === 0 }
    );

  const flags = analysis?.flags;
  const accentColor = flags?.hot
    ? theme.palette.warning.main
    : flags?.bounced || flags?.unsubscribed
    ? theme.palette.error.light
    : flags?.cold
    ? theme.palette.text.disabled
    : "transparent";

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      disableGutters
      elevation={0}
      sx={{
        mb: 1,
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        borderLeft: `3px solid ${accentColor}`,
        borderRadius: "8px !important",
        "&::before": { display: "none" },
        overflow: "hidden",
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ width: "100%", overflow: "hidden" }}
        >
          <Box sx={{ minWidth: 180 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {customer.CustomerName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              #{customer.CustomerNo}
            </Typography>
          </Box>

          {flags && hasAnyFlag(flags) && (
            <CustomerFlagBadges flags={flags} stats={analysis!.stats} />
          )}

          {customer.ByCampaign && customer.ByCampaign.length > 0 && (
            <Tooltip
              title={customer.ByCampaign
                .map((c) => `${c.CampaignName} (${c.Count})`)
                .join("\n")}
            >
              <Chip
                icon={<CampaignIcon sx={{ fontSize: 14 }} />}
                label={customer.ByCampaign.length}
                size="small"
                variant="outlined"
                color={
                  highlightedCampaignIds.length > 0 &&
                  customer.ByCampaign.some((c) =>
                    highlightedCampaignIds.includes(c.CampaignId)
                  )
                    ? "primary"
                    : "default"
                }
                sx={{ height: 22 }}
              />
            </Tooltip>
          )}

          {customer.ByType.length > 0 && (
            <Stack
              direction="row"
              spacing={0.5}
              flexWrap="wrap"
              useFlexGap
              sx={{ flex: 1, overflow: "hidden" }}
            >
              {customer.ByType.map((t) => (
                <Box
                  key={t.Type}
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.dark,
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  {BrazeCurrentEventTypeLabels[t.Type] ?? `Typ ${t.Type}`} (
                  {t.Count})
                </Box>
              ))}
            </Stack>
          )}

          {customer.ByType.length === 0 && <Box sx={{ flex: 1 }} />}
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0 }}>
        {customer.ByCampaign && customer.ByCampaign.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.75, fontWeight: 600 }}
            >
              Kampanjer
            </Typography>
            <Stack spacing={0.5}>
              {customer.ByCampaign.map((c) => (
                <CampaignRow
                  key={c.CampaignId}
                  campaign={c}
                  highlighted={highlightedCampaignIds.includes(c.CampaignId)}
                />
              ))}
            </Stack>
          </Box>
        )}

        {customer.ContactPersons.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {customer.ContactPersons.map((cp) => (
              <Stack
                key={cp.ContactPersonNo}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  py: 0.75,
                  px: 1,
                  borderRadius: 1,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PersonIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                  <Typography variant="body2">
                    {cp.ContactPersonName}
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color="text.secondary"
                >
                  {cp.TotalItems}
                </Typography>
              </Stack>
            ))}
          </Box>
        )}

        {detailsFetching && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {details && details.Items.length > 0 && (
          <TableContainer sx={{ mb: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Skapad</TableCell>
                  <TableCell>Kontaktperson</TableCell>
                  <TableCell>Typ</TableCell>
                  <TableCell>Kampanj</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {details.Items.map((item) => (
                  <TableRow
                    key={item.CurrentQueueDetailNo}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/customer/${item.CustomerNo}`)}
                  >
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(item.Created)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.ContactPersonName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          BrazeCurrentEventTypeLabels[
                            item.CurrentQueueDetailType
                          ] ?? `Typ ${item.CurrentQueueDetailType}`
                        }
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getCampaignName(item)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {details && details.HasNextPage && (
          <Button size="small" onClick={onDrillDown}>
            Visa alla ({customer.TotalQueueItems} poster)
          </Button>
        )}

        {customer.TotalQueueItems > 0 &&
          details &&
          !details.HasNextPage &&
          details.Items.length > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              Visar alla {details.Items.length} poster
            </Typography>
          )}
      </AccordionDetails>
    </Accordion>
  );
};

// =============================================================================
// Details view (full drill-down via search endpoint)
// =============================================================================

interface DetailsViewProps {
  customerNo: number | null;
  fromDate: string;
  toDate: string;
  onBack: () => void;
}

const DetailsView: React.FC<DetailsViewProps> = ({
  customerNo,
  fromDate,
  toDate,
  onBack,
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState<CurrentQueueDetailSearchRequest>({
    page: 1,
    pageSize: 50,
    sortBy: "created",
    sortDir: "desc",
    customerNo: customerNo ?? undefined,
    fromDate,
    toDate,
  });
  const [searchInput, setSearchInput] = useState("");

  const { data, isLoading, isFetching } =
    useSearchCurrentQueueDetailsQuery(query);

  const handleSort = (field: SortField) => {
    setQuery((prev) => ({
      ...prev,
      sortBy: field,
      sortDir:
        prev.sortBy === field && prev.sortDir === "asc" ? "desc" : "asc",
      page: 1,
    }));
  };

  const handleSearch = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      setQuery((prev) => ({
        ...prev,
        search: searchInput || undefined,
        page: 1,
      }));
    }
  };

  const handlePageChange = (direction: "next" | "prev") => {
    setQuery((prev) => ({
      ...prev,
      page:
        direction === "next" ? (prev.page ?? 1) + 1 : (prev.page ?? 1) - 1,
    }));
  };

  const handleRowClick = (item: CurrentQueueDetailItem) => {
    if (item.CustomerNo) {
      navigate(`/customer/${item.CustomerNo}`);
    }
  };

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          Översikt
        </Button>
        {customerNo && data?.Items?.[0]?.CustomerName && (
          <Typography variant="h6" fontWeight={600}>
            {data.Items[0].CustomerName}
          </Typography>
        )}
      </Stack>

      <Paper sx={{ mb: 2, p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Sök..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearch}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ minWidth: 250 }}
          />
          <Chip
            icon={<FilterListIcon />}
            label={query.processed === false ? "Ej behandlade" : "Alla"}
            variant={query.processed === false ? "filled" : "outlined"}
            color={query.processed === false ? "primary" : "default"}
            onClick={() =>
              setQuery((prev) => ({
                ...prev,
                processed: prev.processed === false ? undefined : false,
                page: 1,
              }))
            }
          />
        </Stack>
      </Paper>

      <TableContainer component={Paper} sx={{ position: "relative" }}>
        {(isLoading || isFetching) && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.7)",
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={query.sortBy === "created"}
                  direction={
                    query.sortBy === "created" ? query.sortDir : "desc"
                  }
                  onClick={() => handleSort("created")}
                >
                  Skapad
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={query.sortBy === "customerno"}
                  direction={
                    query.sortBy === "customerno" ? query.sortDir : "desc"
                  }
                  onClick={() => handleSort("customerno")}
                >
                  Kund
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={query.sortBy === "contactpersonno"}
                  direction={
                    query.sortBy === "contactpersonno"
                      ? query.sortDir
                      : "desc"
                  }
                  onClick={() => handleSort("contactpersonno")}
                >
                  Kontaktperson
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={query.sortBy === "type"}
                  direction={
                    query.sortBy === "type" ? query.sortDir : "desc"
                  }
                  onClick={() => handleSort("type")}
                >
                  Typ
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={query.sortBy === "detailid"}
                  direction={
                    query.sortBy === "detailid" ? query.sortDir : "desc"
                  }
                  onClick={() => handleSort("detailid")}
                >
                  Kampanj
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.Items?.map((item) => (
              <TableRow
                key={item.CurrentQueueDetailNo}
                hover
                sx={{ cursor: item.CustomerNo ? "pointer" : "default" }}
                onClick={() => handleRowClick(item)}
              >
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(item.Created)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {item.CustomerName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    #{item.CustomerNo}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {item.ContactPersonName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={
                      BrazeCurrentEventTypeLabels[
                        item.CurrentQueueDetailType
                      ] ?? `Typ ${item.CurrentQueueDetailType}`
                    }
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {item.CurrentQueueDetailId}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && (!data?.Items || data.Items.length === 0) && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    Inga poster hittades
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {data && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 2, px: 1 }}
        >
          <Typography variant="body2" color="text.secondary">
            Sida {data.Page} &middot; {data.Items?.length ?? 0} resultat
          </Typography>
          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              disabled={!data.HasPreviousPage}
              onClick={() => handlePageChange("prev")}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              size="small"
              disabled={!data.HasNextPage}
              onClick={() => handlePageChange("next")}
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Stack>
      )}
    </>
  );
};

// =============================================================================
// Per-customer flag badges (icon-only with tooltip)
// =============================================================================

interface CustomerFlagBadgesProps {
  flags: CustomerFlags;
  stats: CustomerStats;
}

const CustomerFlagBadges: React.FC<CustomerFlagBadgesProps> = ({
  flags,
  stats,
}) => {
  const badges: Array<{
    key: string;
    title: string;
    icon: React.ReactElement;
    color: "warning" | "error" | "info" | "success" | "default";
  }> = [];

  if (flags.hot) {
    const reason = stats.click > 0
      ? `Klickat ${stats.click} gång${stats.click === 1 ? "" : "er"}`
      : `Hög open-rate (${Math.round(stats.openRate * 100)}%) jämfört med snittet`;
    badges.push({
      key: "hot",
      title: `Het kund — ${reason}`,
      icon: <LocalFireDepartmentIcon sx={{ fontSize: 14 }} />,
      color: "warning",
    });
  }
  if (flags.clickWithoutOpen) {
    badges.push({
      key: "fwd",
      title: "Klick utan open — kan vara vidareforwardad eller bildladdning blockerad",
      icon: <ForwardIcon sx={{ fontSize: 14 }} />,
      color: "info",
    });
  }
  if (flags.cold) {
    badges.push({
      key: "cold",
      title: `Kall — ${stats.sent} skickade, 0 öppnade`,
      icon: <AcUnitIcon sx={{ fontSize: 14 }} />,
      color: "default",
    });
  }
  if (flags.unsubscribed) {
    badges.push({
      key: "unsub",
      title: `Avprenumererad (${stats.unsubscribe})`,
      icon: <BlockIcon sx={{ fontSize: 14 }} />,
      color: "error",
    });
  }
  if (flags.bounced) {
    badges.push({
      key: "bounce",
      title: `Bounce (${stats.bounce})`,
      icon: <ReportProblemIcon sx={{ fontSize: 14 }} />,
      color: "error",
    });
  }

  return (
    <Stack direction="row" spacing={0.5}>
      {badges.map((b) => (
        <Tooltip key={b.key} title={b.title}>
          <Chip
            icon={b.icon}
            label=""
            size="small"
            color={b.color}
            variant={b.color === "default" ? "outlined" : "filled"}
            sx={{
              height: 22,
              width: 28,
              "& .MuiChip-icon": { marginLeft: "8px", marginRight: 0 },
              "& .MuiChip-label": { display: "none" },
            }}
          />
        </Tooltip>
      ))}
    </Stack>
  );
};

// =============================================================================
// Campaign row inside customer accordion
// =============================================================================

interface CampaignRowProps {
  campaign: SalesSummaryByCampaign;
  highlighted: boolean;
}

const CampaignRow: React.FC<CampaignRowProps> = ({ campaign, highlighted }) => {
  const theme = useTheme();
  const counts = campaignCounts(campaign);
  const openRate = counts.sent ? Math.round((counts.open / counts.sent) * 100) : 0;
  const clickRate = counts.sent
    ? Math.round((counts.click / counts.sent) * 100)
    : 0;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        py: 0.5,
        px: 1,
        borderRadius: 1,
        bgcolor: highlighted
          ? alpha(theme.palette.primary.main, 0.08)
          : "transparent",
        borderLeft: highlighted
          ? `3px solid ${theme.palette.primary.main}`
          : `3px solid transparent`,
      }}
    >
      <CampaignIcon fontSize="small" sx={{ color: "text.secondary" }} />
      <Typography variant="body2" sx={{ flex: 1, minWidth: 160 }}>
        {campaign.CampaignName}
      </Typography>

      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {campaign.ByType.map((t) => (
          <Box
            key={t.Type}
            sx={{
              px: 0.75,
              py: 0.125,
              borderRadius: 0.75,
              bgcolor: alpha(theme.palette.info.main, 0.1),
              color: theme.palette.info.dark,
              fontSize: "0.7rem",
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {BrazeCurrentEventTypeLabels[t.Type] ?? `Typ ${t.Type}`} ({t.Count})
          </Box>
        ))}
      </Stack>

      {counts.sent > 0 && (
        <Tooltip
          title={`Open rate ${openRate}% (${counts.open}/${counts.sent}) · Click rate ${clickRate}% (${counts.click}/${counts.sent})`}
        >
          <Stack direction="row" spacing={0.5}>
            <Chip
              size="small"
              label={`${openRate}% open`}
              variant="outlined"
              sx={{ height: 20, fontSize: "0.65rem" }}
            />
            <Chip
              size="small"
              label={`${clickRate}% click`}
              variant="outlined"
              color={clickRate > 0 ? "success" : "default"}
              sx={{ height: 20, fontSize: "0.65rem" }}
            />
          </Stack>
        </Tooltip>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: 32, textAlign: "right" }}
      >
        {campaign.Count}
      </Typography>
    </Stack>
  );
};

// --- Helpers ---

const formatDate = (dateStr: string) => {
  try {
    return format(new Date(dateStr), "d MMM yyyy HH:mm", { locale: sv });
  } catch {
    return dateStr;
  }
};

const getCampaignName = (item: CurrentQueueDetailItem): string => {
  try {
    const props = JSON.parse(item.CurrentQueueDetailProperties);
    return props.campaign_name || props.canvas_name || item.CurrentQueueDetailId;
  } catch {
    return item.CurrentQueueDetailId;
  }
};

const campaignCounts = (campaign: SalesSummaryByCampaign) => {
  let sent = 0;
  let open = 0;
  let click = 0;
  for (const t of campaign.ByType) {
    if (t.Type === BrazeCurrentEventType.UsersMessagesEmailSend) sent += t.Count;
    else if (t.Type === BrazeCurrentEventType.UsersMessagesEmailOpen)
      open += t.Count;
    else if (t.Type === BrazeCurrentEventType.UsersMessagesEmailClick)
      click += t.Count;
  }
  return { sent, open, click };
};

const matchesEngagement = (
  status: EngagementStatus,
  stats: CustomerStats | undefined
): boolean => {
  if (!stats) return false;
  switch (status) {
    case "never_opened":
      return stats.sent > 0 && stats.open === 0 && stats.click === 0;
    case "opened_not_clicked":
      return stats.open > 0 && stats.click === 0;
    case "clicked":
      return stats.click > 0;
    case "negative":
      return stats.unsubscribe > 0 || stats.bounce > 0;
    case "all":
    default:
      return true;
  }
};

// =============================================================================
// Engagement analysis (population stats + per-customer outlier/anomaly flags)
// =============================================================================

interface CustomerStats {
  sent: number;
  open: number;
  click: number;
  unsubscribe: number;
  bounce: number;
  openRate: number;
  clickRate: number;
}

interface CustomerFlags {
  hot: boolean;
  cold: boolean;
  clickWithoutOpen: boolean;
  unsubscribed: boolean;
  bounced: boolean;
}

export interface CustomerAnalysis {
  stats: CustomerStats;
  flags: CustomerFlags;
}

const COLD_SEND_THRESHOLD = 5;
const HOT_SEND_THRESHOLD = 3;

const customerStats = (customer: SalesSummaryCustomer): CustomerStats => {
  let sent = 0;
  let open = 0;
  let click = 0;
  let unsubscribe = 0;
  let bounce = 0;
  for (const c of customer.ByCampaign ?? []) {
    for (const t of c.ByType) {
      switch (t.Type) {
        case BrazeCurrentEventType.UsersMessagesEmailSend:
          sent += t.Count;
          break;
        case BrazeCurrentEventType.UsersMessagesEmailOpen:
          open += t.Count;
          break;
        case BrazeCurrentEventType.UsersMessagesEmailClick:
          click += t.Count;
          break;
        case BrazeCurrentEventType.UsersMessagesEmailUnsubscribe:
          unsubscribe += t.Count;
          break;
        case BrazeCurrentEventType.UsersMessagesEmailBounce:
        case BrazeCurrentEventType.UsersMessagesEmailSoftBounce:
          bounce += t.Count;
          break;
      }
    }
  }
  return {
    sent,
    open,
    click,
    unsubscribe,
    bounce,
    openRate: sent ? open / sent : 0,
    clickRate: sent ? click / sent : 0,
  };
};

const analyzeCustomers = (
  customers: SalesSummaryCustomer[]
): Map<number, CustomerAnalysis> => {
  const rows = customers.map((c) => ({
    no: c.CustomerNo,
    stats: customerStats(c),
  }));

  const sample = rows.filter((r) => r.stats.sent >= HOT_SEND_THRESHOLD);
  const meanOpen = sample.length
    ? sample.reduce((a, x) => a + x.stats.openRate, 0) / sample.length
    : 0;
  const stdOpen = sample.length
    ? Math.sqrt(
        sample.reduce((a, x) => a + (x.stats.openRate - meanOpen) ** 2, 0) /
          sample.length
      )
    : 0;

  const map = new Map<number, CustomerAnalysis>();
  for (const { no, stats } of rows) {
    const enoughSends = stats.sent >= HOT_SEND_THRESHOLD;
    const openOutlier =
      enoughSends && stdOpen > 0 && stats.openRate > meanOpen + stdOpen;
    const hot = stats.click > 0 || openOutlier;
    const cold =
      stats.sent >= COLD_SEND_THRESHOLD &&
      stats.open === 0 &&
      stats.click === 0;
    map.set(no, {
      stats,
      flags: {
        hot,
        cold,
        clickWithoutOpen: stats.click > 0 && stats.open === 0,
        unsubscribed: stats.unsubscribe > 0,
        bounced: stats.bounce > 0,
      },
    });
  }
  return map;
};

const hasAnyFlag = (flags: CustomerFlags): boolean =>
  flags.hot ||
  flags.cold ||
  flags.clickWithoutOpen ||
  flags.unsubscribed ||
  flags.bounced;

const aggregateCampaigns = (
  customers: SalesSummaryCustomer[]
): CampaignRollupEntry[] => {
  const map = new Map<string, CampaignRollupEntry>();
  for (const customer of customers) {
    if (!customer.ByCampaign) continue;
    for (const c of customer.ByCampaign) {
      const existing = map.get(c.CampaignId) ?? {
        CampaignId: c.CampaignId,
        CampaignName: c.CampaignName,
        Count: 0,
        CustomerCount: 0,
        Sent: 0,
        Open: 0,
        Click: 0,
      };
      existing.Count += c.Count;
      existing.CustomerCount += 1;
      const counts = campaignCounts(c);
      existing.Sent += counts.sent;
      existing.Open += counts.open;
      existing.Click += counts.click;
      map.set(c.CampaignId, existing);
    }
  }
  return Array.from(map.values()).sort((a, b) => b.Count - a.Count);
};

export default MyContactsView;
